import React, { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { cn } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Close } from "@dreamhub/designsystem/icons";
import StripBanner from "@/components/ui/strip-banner";
import { DashedBorder } from "@/components/ui/dashed-border";
import RevenueItemsTable from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/components/RevenueItemsTable";
import { DraftRevenueItem } from "@/pages/contracts/components/termsDraft/types";
import { ContractRevenueItem } from "@/pages/contracts/types";
import { CreateRevenueItemRequest } from "@/pages/contracts/hooks";
import TermsCalculatedValuesFooter from "./TermsCalculatedValuesFooter";
import {
  TermsCalculatedValues,
  TermsChangeSet,
  TermsUpdateKind
} from "./types";
import { buildTermsColumnChanges } from "./termsChangeMap";
import { TermsFieldSettings } from "./types";

// --app-content-h subtracts only the 64px nav, not the shell's 80px inset.
const PAGE_HEIGHT_CLASS =
  "flex flex-col h-full max-h-[calc(var(--app-content-h)-16px)] p-2";

// `border-dashed` leaves the dash lengths to the browser, so the frame is drawn
// by DashedBorder instead. These are the design's exact values.
const DASH_STROKE_WIDTH = 2.2;
const DASH_LENGTH = 4;
const DASH_GAP = 6;
// Matches the container's rounded-xl, measured to the stroke's outer edge.
const CONTAINER_RADIUS = 12;

interface Props {
  updateKind: TermsUpdateKind;
  contractName: string;
  // Figma tints the strip per update kind: blue for an amendment, gold for a correction.
  bannerClassName: string;
  items: DraftRevenueItem[];
  baseline: Record<string, ContractRevenueItem>;
  productId?: string;
  currency: string | null;
  calculatedValues: TermsCalculatedValues;
  isCalculating: boolean;
  isCalculationStale: boolean;
  fieldSettings: TermsFieldSettings | undefined;
  // A new line starts here; existing lines keep their own start date.
  effectiveDate?: string;
  changeSet: TermsChangeSet | null;
  onAddItem: (request: CreateRevenueItemRequest) => void;
  onUpdateItem: (id: string, request: CreateRevenueItemRequest) => void;
  onDeleteItem: (id: string) => void;
  onRestoreItem: (id: string) => void;
  onRevertItem: (id: string) => void;
  onClose: () => void;
  // Saving belongs to CRM-9211 / CRM-9212; this editor owns the pending set only.
  footerActions?: React.ReactNode;
  // The Amendment flow's effective-date block sits above the table.
  headerSection?: React.ReactNode;
}

// The pending-set editor both Update terms flows share; nothing is written here.
const UpdateTermsEditor: React.FC<Props> = ({
  updateKind,
  contractName,
  bannerClassName,
  items,
  baseline,
  productId,
  currency,
  calculatedValues,
  isCalculating,
  isCalculationStale,
  fieldSettings,
  effectiveDate,
  changeSet,
  onAddItem,
  onUpdateItem,
  onDeleteItem,
  onRestoreItem,
  onRevertItem,
  onClose,
  footerActions,
  headerSection
}) => {
  const { t } = useTranslation();
  const columnChangesByItem = useMemo(
    () => buildTermsColumnChanges(changeSet),
    [changeSet]
  );

  const closeButton = (
    <IconButton
      variant="iconNoBg"
      size="lg"
      onClick={onClose}
      aria-label={t("close")}
      data-test="update-terms-editor-close"
    >
      <Close />
    </IconButton>
  );

  return (
    <div className={PAGE_HEIGHT_CLASS} data-test="update-terms-editor">
      <StripBanner
        className={cn("shrink-0 rounded-t-xl", bannerClassName)}
        data-test="update-terms-banner"
      >
        <Text variant="body" className="text-center">
          <Trans
            i18nKey={
              updateKind === TermsUpdateKind.CORRECTION
                ? "contracts.updateTerms.editor.bannerCorrection"
                : "contracts.updateTerms.editor.bannerAmendment"
            }
            values={{ contractName }}
            components={[
              // Routed through onClose, not a link, so leaving passes the discard guard.
              <button
                key="contract"
                type="button"
                className="text-link hover:underline"
                onClick={onClose}
                data-test="update-terms-banner-contract-link"
              />
            ]}
          />
        </Text>
      </StripBanner>
      <DashedBorder
        className="z-10 -mt-3 flex flex-col flex-1 min-h-0 rounded-xl bg-surface-recessed"
        strokeWidth={DASH_STROKE_WIDTH}
        dashLength={DASH_LENGTH}
        gapLength={DASH_GAP}
        // rx is measured at the stroke's centre line, so half the width comes off
        // to put its outer edge on the container's own rounded-xl edge.
        borderRadius={CONTAINER_RADIUS - DASH_STROKE_WIDTH / 2}
        // Round caps would lengthen each dash and shorten each gap by 1.1px.
        strokeLinecap="butt"
        data-test="update-terms-editor-outline"
      >
        <div className="flex flex-col flex-1 min-h-0 overflow-y-auto p-6 gap-8">
          {/* The close button rides on whichever block comes first, so a
              correction — which has no header section — starts at the padding
              rather than below an otherwise empty row. */}
          {headerSection ? (
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">{headerSection}</div>
              {closeButton}
            </div>
          ) : null}
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-1 min-w-0">
                <Text variant="smSubtitle">
                  {t("contracts.updateTerms.editor.termsTitle")}
                </Text>
                <Text variant="bodySecondary">
                  {t(
                    updateKind === TermsUpdateKind.CORRECTION
                      ? "contracts.updateTerms.editor.termsSubtitleCorrection"
                      : "contracts.updateTerms.editor.termsSubtitleAmendment"
                  )}
                </Text>
              </div>
              {headerSection ? null : closeButton}
            </div>
            <div className="flex flex-col">
              <div className="relative z-10 flex flex-col w-full bg-surface-elevation0 rounded-xl border border-border-border1 overflow-hidden">
                <RevenueItemsTable
                  revenueItems={items}
                  productId={productId}
                  currency={currency}
                  showStatusColumn={false}
                  termsFieldSettings={fieldSettings}
                  lockServiceStartDate
                  lockBillingStartDate
                  allowServiceEndDateEdit
                  effectiveDate={effectiveDate}
                  columnChangesByItem={columnChangesByItem}
                  draft={{
                    baseline,
                    showSelection: false,
                    selectedIds: [],
                    onToggleSelect: () => {},
                    onToggleSelectAll: () => {},
                    onDeleteItem,
                    onRestoreItem,
                    onRevertItem,
                    onAddItem,
                    onUpdateItem
                  }}
                />
              </div>
              <TermsCalculatedValuesFooter
                values={calculatedValues}
                currency={currency}
                updateKind={updateKind}
                isRefreshing={isCalculating}
                isStale={isCalculationStale}
              />
            </div>
          </div>
        </div>
        {/* A panel inset from the container's edge, not a full-width footer bar. */}
        <div className="shrink-0 p-2">
          <div
            className="flex items-center justify-between gap-2 rounded-xl border border-border-border1 bg-surface-floating-panel px-4 py-3"
            data-test="update-terms-editor-footer"
          >
            <Button
              variant="secondaryNoBg"
              onClick={onClose}
              data-test="update-terms-editor-cancel"
            >
              {t("cancel")}
            </Button>
            <div className="flex items-center gap-2">{footerActions}</div>
          </div>
        </div>
      </DashedBorder>
    </div>
  );
};

export default UpdateTermsEditor;
